import { IOrderWorkLogsHook, IWorkLogTotal } from '@models';
import { OrderWorkLog } from '@services';
import { useStore } from '@stores';
import { workLogMinutes } from '@utils';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const WHOLE_JOB = 'Whole job';

export const sumWorkLogsBy = <TKey>(
  logs: OrderWorkLog[],
  now: dayjs.Dayjs,
  keyOf: (log: OrderWorkLog) => TKey,
  labelOf: (log: OrderWorkLog) => string
): IWorkLogTotal<TKey>[] => {
  const totals = new Map<TKey, IWorkLogTotal<TKey>>();
  logs.forEach((log) => {
    const key = keyOf(log);
    const minutes = workLogMinutes(log, now);
    const current = totals.get(key);
    if (current) {
      current.minutes += minutes;
    } else {
      totals.set(key, { key, label: labelOf(log), minutes });
    }
  });
  return Array.from(totals.values()).sort((a, b) => b.minutes - a.minutes);
};

/**
 * The totals every work-log view shows, from whatever list it holds. Shared by the job's own hook
 * and the build-wide one so the two never disagree on how an open entry is counted.
 */
export const summariseWorkLogs = (dataSource: OrderWorkLog[], now: dayjs.Dayjs) => ({
  totalMinutes: dataSource.reduce((acc, log) => acc + workLogMinutes(log, now), 0),
  minutesByTechnician: sumWorkLogsBy(
    dataSource,
    now,
    (log) => log.userId,
    (log) => log.user?.fullname ?? `#${log.userId}`
  ),
  minutesByService: sumWorkLogsBy(
    dataSource,
    now,
    (log) => log.serviceId ?? null,
    (log) => log.serviceTitle ?? WHOLE_JOB
  ),
});

/** Open entries keep counting, so the caller's "now" ticks once a minute while any exist. */
export const useWorkLogClock = (hasOpen: boolean) => {
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    if (!hasOpen) return undefined;
    setNow(dayjs());
    const id = setInterval(() => setNow(dayjs()), 60000);
    return () => clearInterval(id);
  }, [hasOpen]);
  return now;
};

export const useOrderWorkLogs = (orderId: string | undefined): IOrderWorkLogsHook => {
  const { orderStore } = useStore();
  const { orderWorkLogs, orderWorkLogsLoading } = orderStore;

  useEffect(() => {
    orderId && orderStore.getOrderWorkLogs(orderId);

    return () => {
      orderStore.resetOrderWorkLogs();
    };
  }, [orderStore, orderId]);

  // A job carries a handful of entries, so the totals are recomputed per render rather than memoised.
  const dataSource = Array.from(orderWorkLogs.values());
  const hasOpen = dataSource.some((log) => !log.workedTo);

  const now = useWorkLogClock(hasOpen);

  return {
    loading: orderWorkLogsLoading,
    dataSource,
    hasOpen,
    ...summariseWorkLogs(dataSource, now),
  };
};
